import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

const EditStaff = () => {
  // support either ":staffId" or ":id"
  const params = useParams();
  const staffId = params.staffId || params.id;
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Form state - EXACT same fields as AddStaff
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    employeeId: '',
    contactNumber: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    staffType: '',
    department: '',
    specialization: '',
    photo: null,
    status: 'Active'
  });

  // Course assignments state - EXACT same as AddStaff
  const [courseAssignments, setCourseAssignments] = useState([
    {
      id: 1,
      courseName: '',
      courseSearchQuery: '',
      selectedModuleId: '',
      canTeachPrimary: true,
      canTeachAssistant: true,
      isExisting: false
    }
  ]);

  const [modulesList, setModulesList] = useState([]);
  const [filteredModules, setFilteredModules] = useState({});
  const [showModuleDropdowns, setShowModuleDropdowns] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Track IDs of existing DB assignments user removed
  const [deletedAssignmentIds, setDeletedAssignmentIds] = useState([]);

  // Load staff details and modules
  useEffect(() => {
    const loadStaffData = async () => {
      if (!currentUser || !staffId) return;
      
      try {
        setInitialLoading(true);

        // Get university_id
        let universityId = currentUser?.university_id;
        if (!universityId && currentUser?.id) {
          const { data: adminData } = await supabase
            .from('admins')
            .select('university_id')
            .eq('user_id', currentUser.id)
            .single();
          
          if (adminData) universityId = adminData.university_id;
        }

        // Load staff details
        const { data: staffData, error: staffError } = await supabase
          .from('staff')
          .select('*')
          .eq('id', staffId)
          .eq('university_id', universityId)
          .single();

        if (staffError) {
          alert('Staff member not found');
          navigate('/lecturers');
          return;
        }

        // Map to form data
        setFormData({
          fullName: staffData.name || '',
          email: staffData.email || '',
          password: '', // Don't pre-fill password
          employeeId: staffData.employee_id || '',
          contactNumber: staffData.contact_no || '',
          dateOfBirth: staffData.dob ? staffData.dob.split('T')[0] : '',
          gender: staffData.gender || '',
          address: staffData.address || '',
          staffType: staffData.staff_type || '',
          department: staffData.department || '',
          specialization: staffData.specialization || '',
          photo: null,
          status: staffData.active ? 'Active' : 'Inactive'
        });

        // Load modules
        const { data: modulesData } = await supabase
          .from('modules')
          .select('id, module_name, module_code, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('module_name', { ascending: true });

        setModulesList(modulesData || []);

        // Load existing course assignments
        const { data: courseData } = await supabase
          .from('staff_courses')
          .select(`
            id,
            course_name,
            module_id,
            can_teach_as_primary,
            can_teach_as_assistant,
            modules (
              id,
              module_name,
              module_code
            )
          `)
          .eq('staff_id', staffId);

        if (courseData && courseData.length > 0) {
          const mappedCourses = courseData.map((course, index) => ({
            id: course.id || `existing_${index + 1}`,
            courseName: course.course_name || '',
            courseSearchQuery: course.modules ?
              `${course.modules.module_name} (${course.modules.module_code})` :
              course.course_name || '',
            selectedModuleId: course.module_id || '',
            canTeachPrimary: course.can_teach_as_primary ?? true,
            canTeachAssistant: course.can_teach_as_assistant ?? true,
            isExisting: true
          }));
          setCourseAssignments(mappedCourses);

          const initialFiltered = {};
          mappedCourses.forEach(assignment => {
            initialFiltered[assignment.id] = modulesData || [];
          });
          setFilteredModules(initialFiltered);
        } else {
          // initialize filteredModules for default single row
          setFilteredModules(prev => ({ ...prev, [courseAssignments[0].id]: modulesData || [] }));
        }

      } catch (error) {
        console.error('Error loading staff data:', error);
        alert('Error loading staff details');
        navigate('/lecturers');
      } finally {
        setInitialLoading(false);
      }
    };

    loadStaffData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, staffId, navigate]);

  // Handle form changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    setFormData(prev => ({ ...prev, photo: e.target.files[0] }));
  };

  // Course assignment handlers
  const handleCourseAssignmentChange = (id, field, value) => {
    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === id ? { ...assignment, [field]: value } : assignment
      )
    );
  };

  const handleModuleSearch = (assignmentId, query) => {
    // Ignore searches for existing DB assignments (read-only)
    const assignment = courseAssignments.find(a => a.id === assignmentId);
    if (assignment?.isExisting) return;

    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === assignmentId 
          ? { ...assignment, courseSearchQuery: query }
          : assignment
      )
    );
    
    if (query.trim() === '') {
      setFilteredModules(prev => ({ ...prev, [assignmentId]: modulesList }));
    } else {
      const filtered = modulesList.filter(module => 
        module.module_name.toLowerCase().includes(query.toLowerCase()) ||
        module.module_code.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredModules(prev => ({ ...prev, [assignmentId]: filtered }));
    }
    setShowModuleDropdowns(prev => ({ ...prev, [assignmentId]: true }));
  };

  const handleModuleSelect = (assignmentId, module) => {
    // Ignore selects for existing assignments
    const assignment = courseAssignments.find(a => a.id === assignmentId);
    if (assignment?.isExisting) return;

    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === assignmentId 
          ? { 
              ...assignment, 
              courseName: module.module_name,
              courseSearchQuery: `${module.module_name} (${module.module_code})`,
              selectedModuleId: module.id 
            }
          : assignment
      )
    );
    setShowModuleDropdowns(prev => ({ ...prev, [assignmentId]: false }));
  };

  const addCourseAssignment = () => {
    const newId = `new_${Date.now()}`;
    setCourseAssignments(prev => [
      ...prev,
      {
        id: newId,
        courseName: '',
        courseSearchQuery: '',
        selectedModuleId: '',
        canTeachPrimary: true,
        canTeachAssistant: true,
        isExisting: false
      }
    ]);
    setFilteredModules(prev => ({ ...prev, [newId]: modulesList }));
  };

  const removeCourseAssignment = async (id) => {
    const assignment = courseAssignments.find(a => a.id === id);
    if (!assignment) return;

    console.log('[EditStaff] removeCourseAssignment', { id, isExisting: assignment.isExisting });

    if (assignment.isExisting) {
      // Try immediate delete against staff_courses by id + staff_id (safer than filtering by university_id)
      // explicit window.confirm and defensive logging/validation
      console.log('[EditStaff] delete click', { id, staffId, assignment });
      if (!window.confirm('Delete this assigned module? This will remove the record permanently.')) return;
      try {
        // validate id looks like a DB UUID before calling backend
        const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        if (!uuidRegex.test(id)) {
          console.warn('[EditStaff] assignment id is not a UUID, skipping DB delete and marking for later:', id);
          setDeletedAssignmentIds(prev => Array.from(new Set([...prev, id])));
          setCourseAssignments(prev => prev.filter(assignment => assignment.id !== id));
          return;
        }

        setLoading(true);
        const { error } = await supabase
          .from('staff_courses')
          .delete()
          .eq('id', id)
          .eq('staff_id', staffId);

        if (error) {
          // If deletion fails, fallback to marking for deletion and log full error
          console.error('[EditStaff] immediate delete failed, marking for deletion instead', error);
          setDeletedAssignmentIds(prev => Array.from(new Set([...prev, id])));
        } else {
          // remove locally after successful delete
          setCourseAssignments(prev => prev.filter(assignment => assignment.id !== id));
        }
      } catch (err) {
        console.error('[EditStaff] removeCourseAssignment exception', err);
        // fallback: mark for deletion to attempt on submit
        setDeletedAssignmentIds(prev => Array.from(new Set([...prev, id])));
        // keep item removed from UI
        setCourseAssignments(prev => prev.filter(assignment => assignment.id !== id));
      } finally {
        setLoading(false);
      }
    } else {
      // just remove the newly added row
      setCourseAssignments(prev => prev.filter(assignment => assignment.id !== id));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employeeId || !formData.fullName || !formData.email || !formData.staffType) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      // Get university_id
      let universityId = currentUser?.university_id;
      if (!universityId && currentUser?.id) {
        const { data: adminData } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (adminData) universityId = adminData.university_id;
      }

      // Prepare update data
      const staffUpdateData = {
        name: formData.fullName,
        email: formData.email,
        employee_id: formData.employeeId,
        contact_no: formData.contactNumber,
        dob: formData.dateOfBirth || null,
        gender: formData.gender || null,
        address: formData.address || null,
        staff_type: formData.staffType,
        department: formData.department || null,
        specialization: formData.specialization || null,
        active: formData.status === 'Active',
        updated_at: new Date().toISOString()
      };

      // Only include password if provided
      if (formData.password && formData.password.trim() !== '') {
        staffUpdateData.password = formData.password;
      }

      // Update staff
      const { error: staffError } = await supabase
        .from('staff')
        .update(staffUpdateData)
        .eq('id', staffId)
        .eq('university_id', universityId);

      if (staffError) throw staffError;

      // Delete only assignments user removed (existing ones)
      if (deletedAssignmentIds.length > 0) {
        console.log('[EditStaff] attempting to delete staff_courses ids', deletedAssignmentIds);
        // Only keep likely-real DB ids (UUID v4 / standard 36-char hex with dashes)
        const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        const idsToDelete = deletedAssignmentIds.filter(id => typeof id === 'string' && uuidRegex.test(id));
        if (idsToDelete.length > 0) {
          console.log('[EditStaff] idsToDelete (validated)', idsToDelete);
          try {
            const { error: delErr, status } = await supabase
              .from('staff_courses')
              .delete()
              .in('id', idsToDelete)
              .eq('staff_id', staffId);

            if (delErr) {
              console.error('[EditStaff] bulk delete error', { delErr, status });
              throw delErr;
            }
          } catch (err) {
            // Provide more detailed error to the user and logs
            console.error('[EditStaff] failed to delete staff_courses', err);
            throw new Error(err?.message || err?.error || 'Failed to delete course assignments');
          }
        } else {
          console.warn('[EditStaff] no valid staff_courses ids to delete after validation, skipping delete');
        }
      }

      // Insert only newly added assignments (those with isExisting === false)
      const validNew = courseAssignments.filter(a => !a.isExisting && a.selectedModuleId);
      if (validNew.length > 0) {
        const inserts = validNew.map(a => ({
          staff_id: staffId,
          course_name: a.courseName || '',
          module_id: a.selectedModuleId,
          can_teach_as_primary: a.canTeachPrimary ?? true,
          can_teach_as_assistant: a.canTeachAssistant ?? true,
          created_at: new Date().toISOString()
        }));

        try {
          const { error: insErr, status } = await supabase
            .from('staff_courses')
            .insert(inserts);

          if (insErr) {
            console.error('[EditStaff] insert staff_courses error', { insErr, status, inserts });
            throw insErr;
          }
        } catch (err) {
          console.error('[EditStaff] failed to insert new assignments', err);
          throw new Error(err?.message || err?.error || 'Failed to insert new assignments');
        }
      }

      alert('Staff member updated successfully!');
      navigate('/lecturers');

    } catch (error) {
      console.error('Error updating staff:', error);
      alert(`Error updating staff: ${error.message || error.error || 'unknown'}`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
        <Sidebar />
        <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
          <Header />
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Loading Staff Details...
            </h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        <form onSubmit={handleSubmit}>
          {loading && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                padding: '20px 40px',
                borderRadius: 8,
                textAlign: 'center'
              }}>
                Updating staff member...
              </div>
            </div>
          )}
          
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Edit Staff Details</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Full Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Email <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Password (Leave blank to keep current)
                    </label>
                    <input
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Employee ID <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="employeeId"
                      type="text"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Contact Number
                    </label>
                    <input
                      name="contactNumber"
                      type="tel"
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Date of Birth
                    </label>
                    <input
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Staff Type <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="staffType"
                      value={formData.staffType}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Staff Type</option>
                      <option value="lecturer">Lecturer</option>
                      <option value="professor">Professor</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Department
                    </label>
                    <input
                      name="department"
                      type="text"
                      value={formData.department}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Specialization
                    </label>
                    <input
                      name="specialization"
                      type="text"
                      value={formData.specialization}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Status <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Photo
                    </label>
                    <div style={{width: '100%', height: 120, border: '1px dashed #77838F', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'}}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        style={{opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer'}}
                      />
                      <div style={{color: '#77838F', fontSize: 16, fontFamily: 'Outfit', textAlign: 'center'}}>
                        Click to upload new photo
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address - Full Width */}
                <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    style={{
                      width: '100%', 
                      padding: '15px', 
                      borderRadius: 8, 
                      border: '1px solid #77838F', 
                      fontSize: 16, 
                      fontFamily: 'Outfit',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Course Assignments Section */}
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Course Assignments</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              {courseAssignments.map((assignment, index) => (
                <div key={assignment.id} style={{
                  marginBottom: index === courseAssignments.length - 1 ? '0' : '30px',
                  padding: '20px',
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  background: 'white'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                    <h3 style={{fontSize: 18, fontFamily: 'Outfit', fontWeight: '600', color: '#333', margin: 0}}>
                      Course Assignment {index + 1}
                    </h3>
                    { /* allow remove for both existing (marks deletion) and new (removes locally) */ }
                    <button
                      type="button"
                      onClick={() => removeCourseAssignment(assignment.id)}
                      style={{
                        background: '#DC3545',
                        border: 'none',
                        borderRadius: 6,
                        color: 'white',
                        padding: '8px 12px',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px', alignItems: 'end'}}>
                    {/* Course/Module Name */}
                    <div style={{position: 'relative'}}>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Course/Module Name <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search for module..."
                        value={assignment.courseSearchQuery}
                        onChange={(e) => handleModuleSearch(assignment.id, e.target.value)}
                        onFocus={() => {
                          if (!assignment.isExisting) {
                            setShowModuleDropdowns(prev => ({ ...prev, [assignment.id]: true }));
                            if (!filteredModules[assignment.id]) {
                              setFilteredModules(prev => ({ ...prev, [assignment.id]: modulesList }));
                            }
                          }
                        }}
                        readOnly={assignment.isExisting} // make read-only if existing
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: assignment.selectedModuleId ? '2px solid #28a745' : '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: assignment.isExisting ? '#f5f5f5' : (assignment.selectedModuleId ? '#f8fff8' : 'white'),
                          cursor: assignment.isExisting ? 'default' : 'text'
                        }}
                      />
                      {assignment.isExisting && (
                        <div style={{
                          fontSize: 12,
                          color: '#28a745',
                          marginTop: '2px',
                          fontFamily: 'Outfit'
                        }}>
                          ✓ Assigned: {assignment.courseName || 'Module'}
                        </div>
                      )}
                      {showModuleDropdowns[assignment.id] && !assignment.isExisting && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #77838F',
                          borderTop: 'none',
                          borderRadius: '0 0 8px 8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                          {(filteredModules[assignment.id] || []).length === 0 ? (
                            <div style={{
                              padding: '12px 15px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#888',
                              textAlign: 'center'
                            }}>
                              No modules found
                            </div>
                          ) : (
                            (filteredModules[assignment.id] || []).map(module => (
                              <div
                                key={module.id}
                                onClick={() => handleModuleSelect(assignment.id, module)}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  fontSize: 14,
                                  fontFamily: 'Outfit',
                                  backgroundColor: assignment.selectedModuleId === module.id ? '#e3f2fd' : 'white'
                                }}
                              >
                                {module.module_name} ({module.module_code})
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Can Teach as Primary */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Can Teach as Primary
                      </label>
                      <div style={{display: 'flex', alignItems: 'center', height: 49}}>
                        <label style={{display: 'flex', alignItems: 'center', cursor: assignment.isExisting ? 'not-allowed' : 'pointer', marginRight: '20px'}}>
                          <input
                            type="radio"
                            name={`primary_${assignment.id}`}
                            checked={assignment.canTeachPrimary === true}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachPrimary', true)}
                            disabled={assignment.isExisting}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>Yes</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', cursor: assignment.isExisting ? 'not-allowed' : 'pointer'}}>
                          <input
                            type="radio"
                            name={`primary_${assignment.id}`}
                            checked={assignment.canTeachPrimary === false}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachPrimary', false)}
                            disabled={assignment.isExisting}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>No</span>
                        </label>
                      </div>
                    </div>

                    {/* Can Teach as Assistant */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Can Teach as Assistant
                      </label>
                      <div style={{display: 'flex', alignItems: 'center', height: 49}}>
                        <label style={{display: 'flex', alignItems: 'center', cursor: assignment.isExisting ? 'not-allowed' : 'pointer', marginRight: '20px'}}>
                          <input
                            type="radio"
                            name={`assistant_${assignment.id}`}
                            checked={assignment.canTeachAssistant === true}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachAssistant', true)}
                            disabled={assignment.isExisting}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>Yes</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', cursor: assignment.isExisting ? 'not-allowed' : 'pointer'}}>
                          <input
                            type="radio"
                            name={`assistant_${assignment.id}`}
                            checked={assignment.canTeachAssistant === false}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachAssistant', false)}
                            disabled={assignment.isExisting}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Course Button */}
              <div style={{marginTop: '20px', textAlign: 'center'}}>
                <button
                  type="button"
                  onClick={addCourseAssignment}
                  style={{
                    background: '#28A745',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: 16,
                    fontFamily: 'Outfit',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                >
                  <span style={{fontSize: '18px'}}>+</span>
                  Add Course
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{margin: '40px', display: 'flex', justifyContent: 'space-between'}}>
            <Link to="/lecturers">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </Link>
            <button 
              type="submit" 
              disabled={loading}
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: loading ? '#ccc' : '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Updating...' : 'Update Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStaff;